import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTenantContextStore } from '../store/tenant-context.store';
import { beaconApi } from '../services/api';
import {
  Radio,
  Pencil,
  Trash2,
  Loader2,
  Signal,
  Thermometer,
  Droplets,
  Battery,
  Search,
  X,
  Bluetooth,
  AlertCircle,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import clsx from 'clsx';

// ============================================================================
// Types
// ============================================================================

interface Beacon {
  id: string;
  tenantId: string;
  macAddress: string;
  name: string | null;
  type: 'IBEACON' | 'EDDYSTONE';
  uuid: string | null;
  major: number | null;
  minor: number | null;
  namespace: string | null;
  instance: string | null;
  assignedDeviceId: string | null;
  assignedDriverId: string | null;
  lastRssi: number | null;
  lastBattery: number | null;
  lastTemperature: number | null;
  lastHumidity: number | null;
  lastSeen: string | null;
  active: boolean;
  createdAt: string;
  assignedDevice: { id: string; name: string; imei: string } | null;
  assignedDriver: { id: string; name: string } | null;
}

interface BeaconEvent {
  id: string;
  eventType: 'NEAR' | 'AWAY' | 'LOST' | 'DISCOVERED';
  rssi: number | null;
  battery: number | null;
  timestamp: string;
  device: { id: string; name: string } | null;
  beacon: { id: string; name: string | null; macAddress: string } | null;
}

interface BeaconSensorLog {
  id: string;
  temperature: number | null;
  humidity: number | null;
  battery: number | null;
  rssi: number | null;
  recordedAt: string;
}

interface BeaconStats {
  total: number;
  active: number;
  online: number;
  unassigned: number;
  byType: Record<string, number>;
}

// ============================================================================
// Main component
// ============================================================================

export default function BeaconsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'beacons' | 'events' | 'sensors'>('beacons');
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';

  const tabs = [
    { id: 'beacons' as const, label: t('beacons.tab_beacons'), icon: Radio },
    { id: 'events' as const, label: t('beacons.tab_events'), icon: Signal },
    { id: 'sensors' as const, label: t('beacons.tab_sensors'), icon: Thermometer },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-xl">
            <Radio className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('beacons.title')}</h1>
            <p className="text-sm text-gray-500">{t('beacons.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <BeaconStatsCards tenantKey={tenantKey} />

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1 justify-center',
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'beacons' && <BeaconsTab tenantKey={tenantKey} />}
      {activeTab === 'events' && <EventsTab tenantKey={tenantKey} />}
      {activeTab === 'sensors' && <SensorsTab tenantKey={tenantKey} />}
    </div>
  );
}

// ============================================================================
// Stats Cards
// ============================================================================

function BeaconStatsCards({ tenantKey }: { tenantKey: string }) {
  const { t } = useTranslation();
  const { data: stats } = useQuery<BeaconStats>({
    queryKey: ['beacon-stats', tenantKey],
    queryFn: async () => (await beaconApi.getStats()).data,
  });

  const cards = [
    { label: t('beacons.stat_total'), value: stats?.total ?? '-', icon: Radio, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: t('beacons.stat_online'), value: stats?.online ?? '-', icon: Signal, color: 'text-green-600', bg: 'bg-green-50' },
    { label: t('beacons.stat_active'), value: stats?.active ?? '-', icon: Bluetooth, color: 'text-cyan-600', bg: 'bg-cyan-50' },
    { label: t('beacons.stat_unassigned'), value: stats?.unassigned ?? '-', icon: AlertCircle, color: 'text-orange-600', bg: 'bg-orange-50' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <div key={card.label} className="glass-card p-4">
          <div className="flex items-center gap-3">
            <div className={clsx('p-2 rounded-lg', card.bg)}>
              <card.icon className={clsx('h-5 w-5', card.color)} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              <p className="text-xs text-gray-500">{card.label}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Beacons Tab
// ============================================================================

function BeaconsTab({ tenantKey }: { tenantKey: string }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [editBeacon, setEditBeacon] = useState<Beacon | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Beacon | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ data: Beacon[]; pagination: { total: number } }>({
    queryKey: ['beacons', search, tenantKey],
    queryFn: async () => (await beaconApi.list({ search: search || undefined, limit: 200 })).data,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => beaconApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beacons'] });
      queryClient.invalidateQueries({ queryKey: ['beacon-stats'] });
      setDeleteTarget(null);
    },
  });

  const beacons = data?.data || [];

  function getStatusBadge(beacon: Beacon) {
    if (!beacon.active) {
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">{t('beacons.status_inactive')}</span>;
    }
    if (beacon.lastSeen) {
      const lastSeen = new Date(beacon.lastSeen);
      const diff = Date.now() - lastSeen.getTime();
      if (diff < 10 * 60 * 1000) {
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">{t('beacons.status_online')}</span>;
      }
    }
    return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">{t('beacons.status_offline')}</span>;
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('beacons.search_placeholder')}
          className="w-full pl-10 pr-4 py-2 rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : beacons.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Radio className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>{t('beacons.no_beacon')}</p>
          <p className="text-sm mt-1">{t('beacons.no_beacon_hint')}</p>
        </div>
      ) : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-3 font-medium text-gray-500">{t('beacons.th_name')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">{t('beacons.th_mac')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500 hidden md:table-cell">{t('beacons.th_type')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500 hidden lg:table-cell">{t('beacons.th_vehicle')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500 hidden lg:table-cell">{t('beacons.th_driver')}</th>
                <th className="text-center px-4 py-3 font-medium text-gray-500 hidden md:table-cell">{t('beacons.th_rssi')}</th>
                <th className="text-center px-4 py-3 font-medium text-gray-500 hidden md:table-cell">{t('beacons.th_battery')}</th>
                <th className="text-center px-4 py-3 font-medium text-gray-500 hidden xl:table-cell">{t('beacons.th_temp')}</th>
                <th className="text-center px-4 py-3 font-medium text-gray-500 hidden xl:table-cell">{t('beacons.th_humidity')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">{t('beacons.th_status')}</th>
                <th className="text-center px-4 py-3 font-medium text-gray-500 hidden md:table-cell">{t('beacons.th_last_seen')}</th>
                <th className="text-right px-4 py-3 font-medium text-gray-500">{t('beacons.th_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {beacons.map((b) => (
                <tr key={b.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Bluetooth className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="font-medium text-gray-900">
                        {b.name || t('beacons.no_name')}
                      </span>
                      {!b.assignedDeviceId && !b.assignedDriverId && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-orange-100 text-orange-600">
                          {t('beacons.new_badge')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{b.macAddress}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className={clsx(
                      'px-2 py-0.5 text-xs font-medium rounded-full',
                      b.type === 'IBEACON' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    )}>
                      {b.type === 'IBEACON' ? 'iBeacon' : 'Eddystone'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">
                    {b.assignedDevice?.name || '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">
                    {b.assignedDriver?.name || '-'}
                  </td>
                  <td className="px-4 py-3 text-center hidden md:table-cell">
                    {b.lastRssi != null ? (
                      <span className={clsx(
                        'text-xs font-mono',
                        b.lastRssi > -60 ? 'text-green-600' : b.lastRssi > -80 ? 'text-yellow-600' : 'text-red-600'
                      )}>
                        {b.lastRssi} dBm
                      </span>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-3 text-center hidden md:table-cell">
                    {b.lastBattery != null ? (
                      <span className="text-xs">{(b.lastBattery * 100).toFixed(0)}%</span>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-3 text-center hidden xl:table-cell">
                    {b.lastTemperature != null ? `${b.lastTemperature.toFixed(1)}°C` : '-'}
                  </td>
                  <td className="px-4 py-3 text-center hidden xl:table-cell">
                    {b.lastHumidity != null ? `${b.lastHumidity.toFixed(1)}%` : '-'}
                  </td>
                  <td className="px-4 py-3">{getStatusBadge(b)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell text-center">
                    {b.lastSeen ? formatRelative(b.lastSeen) : 'Jamais'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => setEditBeacon(b)}
                        className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                        title={t('beacons.edit')}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(b)}
                        className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"
                        title={t('beacons.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editBeacon && (
        <EditBeaconModal
          beacon={editBeacon}
          tenantKey={tenantKey}
          onClose={() => setEditBeacon(null)}
        />
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="glass-card p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Supprimer le beacon</h3>
            <p className="text-sm text-gray-600 mb-4">
              Supprimer <strong>{deleteTarget.name || deleteTarget.macAddress}</strong> et tout son historique ?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Edit Beacon Modal
// ============================================================================

function EditBeaconModal({
  beacon,
  onClose,
}: {
  beacon: Beacon;
  tenantKey: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(beacon.name || '');
  const [active, setActive] = useState(beacon.active);
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      beaconApi.update(beacon.id, {
        name: name || null,
        active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beacons'] });
      queryClient.invalidateQueries({ queryKey: ['beacon-stats'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Erreur lors de la mise a jour');
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="glass-card p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Modifier le beacon</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">MAC Address</label>
            <input
              type="text"
              value={beacon.macAddress}
              disabled
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm font-mono text-gray-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nom du beacon"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
            </label>
            <span className="text-sm text-gray-700">Actif</span>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded-lg">{error}</div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Events Tab
// ============================================================================

function EventsTab({ tenantKey }: { tenantKey: string }) {
  const [typeFilter, setTypeFilter] = useState<string>('');

  const { data: events, isLoading } = useQuery<BeaconEvent[]>({
    queryKey: ['beacon-events-all', typeFilter, tenantKey],
    queryFn: async () =>
      (await beaconApi.getAllEvents({ type: typeFilter || undefined, limit: 200 })).data,
  });

  const eventTypeLabels: Record<string, { label: string; color: string }> = {
    NEAR: { label: 'Proche', color: 'bg-green-100 text-green-700' },
    AWAY: { label: 'Eloigne', color: 'bg-yellow-100 text-yellow-700' },
    LOST: { label: 'Perdu', color: 'bg-red-100 text-red-700' },
    DISCOVERED: { label: 'Decouvert', color: 'bg-blue-100 text-blue-700' },
  };

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex gap-2">
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-blue-500"
        >
          <option value="">Tous les types</option>
          <option value="NEAR">Proche</option>
          <option value="AWAY">Eloigne</option>
          <option value="LOST">Perdu</option>
          <option value="DISCOVERED">Decouvert</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : !events || events.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Signal className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Aucun evenement de proximite</p>
        </div>
      ) : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-3 font-medium text-gray-500">Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Beacon</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500 hidden md:table-cell">Vehicule</th>
                <th className="text-center px-4 py-3 font-medium text-gray-500 hidden md:table-cell">RSSI</th>
                <th className="text-center px-4 py-3 font-medium text-gray-500 hidden md:table-cell">Batterie</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => {
                const typeInfo = eventTypeLabels[e.eventType] || { label: e.eventType, color: 'bg-gray-100 text-gray-600' };
                return (
                  <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {new Date(e.timestamp).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', typeInfo.color)}>
                        {typeInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{e.beacon?.name || e.beacon?.macAddress || '-'}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                      {e.device?.name || '-'}
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-xs hidden md:table-cell">
                      {e.rssi != null ? `${e.rssi} dBm` : '-'}
                    </td>
                    <td className="px-4 py-3 text-center text-xs hidden md:table-cell">
                      {e.battery != null ? `${(e.battery * 100).toFixed(0)}%` : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Sensors Tab
// ============================================================================

function SensorsTab({ tenantKey }: { tenantKey: string }) {
  const [selectedBeaconId, setSelectedBeaconId] = useState<string>('');

  // Get list of beacons for selection
  const { data: beaconsData } = useQuery<{ data: Beacon[] }>({
    queryKey: ['beacons', '', tenantKey],
    queryFn: async () => (await beaconApi.list({ limit: 200 })).data,
  });

  const beacons = beaconsData?.data || [];
  const beaconId = selectedBeaconId || beacons[0]?.id || '';

  // Get sensor logs for selected beacon
  const { data: sensorLogs, isLoading } = useQuery<BeaconSensorLog[]>({
    queryKey: ['beacon-sensors', beaconId, tenantKey],
    queryFn: async () => (await beaconApi.getSensors(beaconId, { limit: 200 })).data,
    enabled: !!beaconId,
  });

  const chartData = (sensorLogs || [])
    .slice()
    .reverse()
    .map((log) => ({
      time: new Date(log.recordedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      temperature: log.temperature,
      humidity: log.humidity,
      battery: log.battery,
    }));

  return (
    <div className="space-y-4">
      {/* Beacon selector */}
      <div className="flex gap-4 items-center">
        <select
          value={selectedBeaconId}
          onChange={(e) => setSelectedBeaconId(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-blue-500 min-w-[200px]"
        >
          {beacons.length === 0 && <option value="">Aucun beacon</option>}
          {beacons.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name || b.macAddress}
            </option>
          ))}
        </select>
      </div>

      {!beaconId ? (
        <div className="text-center py-12 text-gray-500">
          <Thermometer className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Selectionnez un beacon pour voir les capteurs</p>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : chartData.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Thermometer className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Aucune donnee capteur pour ce beacon</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Temperature Chart */}
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-4">
              <Thermometer className="h-5 w-5 text-red-500" />
              <h3 className="font-semibold text-gray-900">Temperature</h3>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="time" fontSize={11} tick={{ fill: '#9ca3af' }} />
                <YAxis fontSize={11} tick={{ fill: '#9ca3af' }} unit="°C" />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  formatter={(value: unknown) => [`${typeof value === 'number' ? value.toFixed(1) : '-'}°C`, 'Temperature']}
                />
                <Line
                  type="monotone"
                  dataKey="temperature"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Humidity Chart */}
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-4">
              <Droplets className="h-5 w-5 text-blue-500" />
              <h3 className="font-semibold text-gray-900">Humidite</h3>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="time" fontSize={11} tick={{ fill: '#9ca3af' }} />
                <YAxis fontSize={11} tick={{ fill: '#9ca3af' }} unit="%" />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  formatter={(value: unknown) => [`${typeof value === 'number' ? value.toFixed(1) : '-'}%`, 'Humidite']}
                />
                <Line
                  type="monotone"
                  dataKey="humidity"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Battery Chart */}
          <div className="glass-card p-4 lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <Battery className="h-5 w-5 text-green-500" />
              <h3 className="font-semibold text-gray-900">Batterie</h3>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="time" fontSize={11} tick={{ fill: '#9ca3af' }} />
                <YAxis fontSize={11} tick={{ fill: '#9ca3af' }} unit="%" />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  formatter={(value: unknown) => [`${typeof value === 'number' ? value.toFixed(1) : '-'}%`, 'Batterie']}
                />
                <Line
                  type="monotone"
                  dataKey="battery"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function formatRelative(dateStr: string): string {
  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'A l\'instant';
  if (minutes < 60) return `Il y a ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Il y a ${days}j`;
}
